<template>
  <b-form-group :class="required ? 'required-control' : ''">
    <label>{{ label }}</label>
    <Autosuggest
      :model="searchPhone"
      :suggestions="filteredCustomers"
      :placeholder="placeholder"
      :limit="limit"
      :loading="isSearchingCustomer"
      suggestionName="suggestionName"
      @select="onSelectedCustomer"
      @change="onSearchPhoneChange"

    >
      <template slot="custom" slot-scope="{ suggestion }">
        <div class="d-flex flex-column">
          <span class="text-primary">{{ suggestion.item.phoneNo }}</span>
          <small v-if="suggestion.item.fullName" class="font-weight-bold">{{ suggestion.item.fullName }}</small>
          <small v-else-if="suggestion.item.message" class="font-weight-bold">{{ suggestion.item.message }}</small>
        </div>
      </template>
    </Autosuggest>
  </b-form-group>
</template>

<script>
import ApiService from '@/core/services/api.service';
import Autosuggest from '@/view/base/auto-sugguest/AutoSuggest.vue';
import debounce from 'debounce';
import { TIME_TRIGGER } from '@/utils/constants';
import { handleError } from '@/utils/common';

export default {
  name: 'CustomerPhoneSearch',
  components: {
    Autosuggest,
  },
  props: {
    value: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: 'Điện thoại',
    },
    placeholder: {
      type: String,
      default: 'Nhập số điện thoại',
    },
    required: {
      type: Boolean,
      default: false,
    },
    limit: {
      type: Number,
      default: 10,
    },
  },
  data() {
    return {
      searchPhone: '',
      customers: [],
      filteredCustomers: [],
      isSearchingCustomer: false,
      selectedCustomerId: null,
    };
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        if (newVal !== this.searchPhone) {
          this.searchPhone = newVal || '';
        }
      },
    },
  },
  methods: {
    onSearchPhoneChange(text) {
      const phone = text?.trim() || '';
      this.searchPhone = phone;
      this.$emit('input', phone);

      // Clear customer info if phone is empty or too short
      if (!phone || phone.trim().length < 8) {
        this.filteredCustomers = [];
        this.selectedCustomerId = null;
        this.$emit('customer-cleared');
        return;
      }

      // Search customers when phone length >= 8
      if (phone.trim().length >= 8) {
        this.searchCustomerByPhone(phone.trim());
      }
    },
    searchCustomerByPhone: debounce(async function (phone) {
      if (!phone || phone.trim().length < 8) return;
      if (this.isSearchingCustomer) return;

      this.isSearchingCustomer = true;
      try {
        const response = await ApiService.get(
          `v1/customers?customerPhone=${phone}&customerStatus=1`,
        );
        const customers = response.data?.data?.items || [];

        // Map customers to suggestion format
        this.customers = customers.map((customer) => ({
          id: customer.id,
          phoneNo: customer.phoneNo || customer.customerPhone || '',
          fullName: customer.fullName || '',
          email: customer.email || '',
          address: customer.address || '',
          suggestionName: `${customer.phoneNo || customer.customerPhone || ''}`,
        }));

        this.filteredCustomers = [...this.customers];

        // If no customers found, show message
        if (this.customers.length === 0) {
          this.filteredCustomers = [
            {
              id: null,
              phoneNo: phone,
              fullName: '',
              suggestionName: `${phone}`,
              message: 'SĐT không có trong hệ thống',
              notFound: true,
            },
          ];
        }
      } catch (error) {
        handleError(error);
        this.filteredCustomers = [];
      } finally {
        this.isSearchingCustomer = false;
      }
    }, TIME_TRIGGER),
    onSelectedCustomer(option) {
      if (!option || !option.item) {
        this.selectedCustomerId = null;
        this.$emit('customer-found', null);
        this.$emit('customer-cleared');
        return;
      }

      const customer = option.item;

      // Don't process if it's a "not found" message
      if (customer.notFound) {
        return;
      }

      this.selectedCustomerId = customer.id;
      this.searchPhone = customer.phoneNo;
      this.$emit('input', customer.phoneNo);

      // Emit customer data
      const customerData = {
        customerId: customer.id,
        customerName: customer.fullName,
        customerEmail: customer.email,
        customerAddress: customer.address,
        customerPhone: customer.phoneNo,
      };

      this.$emit('customer-found', customerData);
    },
  },
};
</script>

<style lang="scss" scoped>
.customer-phone-search {
  width: 100%;
}
</style>
